import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import './ConfirmDialog.css';

export default function ConfirmDialog({
  open = false,
  title = 'Confirmar ação',
  message = '',
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'danger',
  busy = false,
  onConfirm,
  onCancel,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape' && !busy) onCancel?.();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onCancel]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="confirm-dialog-backdrop"
          role="presentation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => {
            if (!busy) onCancel?.();
          }}
        >
          <motion.div
            className={`confirm-dialog is-${tone}`}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-message"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="confirm-dialog-close"
              aria-label="Fechar"
              disabled={busy}
              onClick={onCancel}
            >
              <FaTimes />
            </button>

            <div className="confirm-dialog-icon" aria-hidden="true">
              <FaExclamationTriangle />
            </div>

            <div className="confirm-dialog-copy">
              <p className="confirm-dialog-kicker">Atenção</p>
              <h3 id="confirm-dialog-title">{title}</h3>
              {message ? (
                <p id="confirm-dialog-message">{message}</p>
              ) : null}
            </div>

            <div className="confirm-dialog-actions">
              <button
                type="button"
                className="confirm-dialog-btn is-ghost"
                disabled={busy}
                onClick={onCancel}
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                className="confirm-dialog-btn is-confirm"
                disabled={busy}
                onClick={onConfirm}
              >
                {busy ? 'Aguarde...' : confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
